import {
  ExclamationCircleOutlined,
  ContainerOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Button, Form, message, Space, Tooltip, Modal, Popconfirm } from 'antd';
import moment from 'moment';
import { FormEvent, useEffect, useState } from 'react';
import { PageContext } from '../../../lib/context';
import mutations from '../../../utils/gql/mutations';
import queries from '../../../utils/gql/queries';
import PaymentsView from './payments';

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  barangay: string;
  created_at: Date;
  comsumer_type: number;
  complete_address: string;
  meter_id: number;
  middle_name: string;
  mobile_number: string;
  municipality: string;
  zip_code: string;
}

const PaymentsController = () => {
  const [getCustomer, { data, error, loading }] = useLazyQuery(queries.GET_CUSTOMER);
  const [total, setTotal] = useState<number>();
  const [limit, setLimit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const [subscribers, setSubscribers] = useState<Customer[]>([]);
  const [active, setActive] = useState<boolean>(true);
  const [show, setShow] = useState<boolean>(false);
  const [visible, setVisible] = useState(false);
  const [info, setInfo] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);
  const [meterInfo, setMeterInfo] = useState<any>();
  const [settingsInfo, setSettingsInfo] = useState<any>();
  const [rateInfo, setRateInfo] = useState<any>();
  const [paymentsInfo, setPaymentsInfo] = useState<any>();
  const [displayAmount, setDisplayAmount] = useState<boolean>(true);

  const [getBilling, { data: billing_data }] = useLazyQuery(
    queries.BILLING_INFO,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [getSettings, { data: settings_data }] = useLazyQuery(
    queries.GET_SETTINGS,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [getPayments, { data: payments_data }] = useLazyQuery(
    queries.GET_PAYMENTS,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [getRate, { data: rate_data }] = useLazyQuery(queries.GET_RATE, {
    fetchPolicy: 'no-cache',
  });

  const [getMeter, { data: meter_data }] = useLazyQuery(queries.GET_METER, {
    fetchPolicy: 'no-cache',
  });

  const usersColumns = [
    { title: 'Subscriber ID', dataIndex: 'id', key: 'id' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Action',
      key: 'action',
      render: (record: any) => (
        <Space>
          <Tooltip title="Show Bills">
            <Button
              type="link"
              onClick={() => {
                getBilling({
                  variables: {
                    subs_id: record.id,
                  },
                });

                getMeter({
                  variables: {
                    id: record.id,
                  },
                });

                getRate({
                  variables: {
                    id: record.id,
                  },
                });

                getSettings({
                  variables: {
                    id: record.id,
                  },
                });

                getPayments({
                  variables: {
                    id: record.id,
                  },
                });

                setInfo(record);
              }}
              icon={<ContainerOutlined />}
              size="middle"
              style={{ width: 50 }}
            ></Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const [insertPayments] = useMutation(mutations.INSERT_PAYMENTS, {
    onCompleted(res) {
      message.success({ content: 'Payment has been received successfully ' });
      _getPayments();
      form.resetFields();
    },
    onError({ graphQLErrors }) {
      message.error('Something is wrong.');
    },
  });

  const _showHandler = () => setShow(true);
  const _closeHandler = () => setShow(false);

  const [getSubscribers, { data: subscriber_data }] = useLazyQuery(
    queries.GET_CUSTOMER,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const handleAmount = (record: any) => {
    if (
      record?.total_balance &&
      record?.total_balance > record?.payments_aggregate?.aggregate?.sum?.amount
    ) {
      setDisplayAmount(true);
    } else {
      setDisplayAmount(false);
    }
  };

  const { confirm } = Modal;
  const _onFinish = (data: any) => {
    {
      confirm({
        title: `A payment of Php ${data.amount} has been received. Please confirm to proceed.`,
        icon: <ExclamationCircleOutlined />,
        onOk() {
          insertPayments({
            variables: {
              data: {
                amount: data.amount,
                billing_id: data.billing_id,
                subscribers_id: data.subscriber_id,
                date: moment().format(),
              },
            },
          });
        },
      });
    }
  };

  const _getPayments = () => {
    getPayments();
  };

  const _switchHandler = () => {
    setActive(!active);
  };

  const _submitHandler = () => {
    form.submit();
  };

  const _getMeter = () => {
    getMeter();
  };

  const [form] = Form.useForm();
  const [form2] = Form.useForm();

  const handleSearch = (value: any) => {
    if (value !== '') {
      getCustomer({
        variables: {
          where: {
            _or: [
              {
                first_name: {
                  _ilike: `%${value}%`,
                },
              },
              {
                last_name: {
                  _ilike: `%${value}%`,
                },
              },
            ],
          },
          limit: limit,
          offset: offset,
          order_by: {
            id: 'desc',
          }
        },
      });
    } else if (value === '') {
      setSubscribers([]);
    }
  };

  useEffect(() => {
    if (billing_data) {
      const data = billing_data.billings[0];
      handleAmount(data);
      setBillingInfo(data);
      setVisible(true);
    }
  }, [billing_data]);

  useEffect(() => {
    if (payments_data) {
      const data = payments_data.payments[0];
      setPaymentsInfo(data);
      setVisible(true);
    }
  }, [payments_data]);

  useEffect(() => {
    if (settings_data) {
      const data = settings_data.settings[0];
      setSettingsInfo(data);
    }
  }, [settings_data]);

  useEffect(() => {
    if (rate_data) {
      const data = rate_data.rates[0];
      setRateInfo(data);
    }
  }, [rate_data]);

  useEffect(() => {
    if (meter_data) {
      const data = meter_data.meters[0];
      setMeterInfo(data);
    }
  }, [meter_data]);

  useEffect(() => {
    if (data) {
      const items = data.subscribers
        .map((subscriber: Customer) => {
          return {
            ...subscriber,
            name: `${subscriber.first_name} ${subscriber.last_name}`,
          };
        })
        .filter((record: any) => record.billings.length > 0);
      setSubscribers(items);
    }
  }, [data]);

  useEffect(() => {
    _getMeter();
  }, [getMeter]);

  const subs_id = (id: number): String => {
    var sub_id = `${id}`;
    while (sub_id.length < 7) {
      sub_id = `0${sub_id}`;
    }
    return sub_id;
  };

  const graphData = {
    labels: ['May', 'June', 'July'],
    datasets: [
      {
        label: 'Monthly Revenue per thousand',
        data: [200, 350, 660, 730, 590],
        backgroundColor: [
          'rgba(255, 99, 132, 0.2)',
          'rgba(255, 206, 86, 0.2)',
          'rgba(54, 162, 235, 0.2)',
        ],
      },
    ],
  };

  const { data: getCustomerTotal } = useQuery(queries.GET_CUSTOMER_TOTAL, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (getCustomerTotal) {
      const temp = getCustomerTotal.subscribers_aggregate;
      setTotal(temp?.aggregate.count)
    }
  }, [getCustomerTotal]);

  const handlePaginationChange = (page: any, pageSize: any) => {
    setLimit(pageSize);
    setCurrent(page);
    if (page * pageSize > pageSize) {
      setOffset(page * pageSize - pageSize);
    }
    if (page === 1) {
      setCurrent(page);
      setOffset(0);
    }
  }

  useEffect(() => {
    if (offset || current) {
      getCustomer({
        variables: {
          limit: limit,
          offset: offset,
          order_by: {
            id: 'desc',
          }
        }
      });
    }
  }, [offset, current]);

  const value: object = {
    subscribers,
    displayAmount,
    usersColumns,
    graphData,
    active,
    show,
    visible,
    info,
    billingInfo,
    meterInfo,
    settingsInfo,
    rateInfo,
    paymentsInfo,
    form,
    form2,
    loading,
    limit,
    offset,
    total,
    current,
    handlePaginationChange,
    getCustomer,
    handleSearch,
    subs_id,
    setVisible,
    _onFinish,
    _showHandler,
    _closeHandler,
    _switchHandler,
    _submitHandler,
  };

  return (
    <PageContext.Provider value={value}>
      <PaymentsView />
    </PageContext.Provider>
  );
};

export default PaymentsController;
